import React, { useCallback, useState } from "react";
import { COLOR_PRIMARY } from "../../styles/colors";
import useGame from "../../hooks/useGame";
import Typography from "@mui/material/Typography";
import GameTimer from "../gameTimer/GameTimer";
import useMediaQuery from "@mui/material/useMediaQuery";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import { useAppSelector } from "../../hooks";
import {
  selectInitialRootIOPS,
  selectRootBlockIOPS,
  selectTrafficPattern,
} from "../../store/game/selectors";
import { OperationTypes } from "../../models/block";
import Box from "@mui/material/Box";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { getCurrentDesignName, setCurrentDesignName } from "../../store";
import IOPSDisplay from "./IOPSDisplay";
import ScoreBoardActions from "./ScoreBoardActions";

const ScoreBoard: React.FC = () => {
  const [gameState, startGame, pauseGame, resumeGame, resetGame] = useGame();
  const isMobile = useMediaQuery(`(max-width: 600px)`);
  const isDesktop = useMediaQuery(`(min-width: 950px)`);
  const isTablet = useMediaQuery(`(max-width: 949px) and (min-width: 601px)`);
  const [designName, setDesignName] = useState<string | null>(
    getCurrentDesignName(),
  );

  const initialIOPS = useAppSelector((state) =>
    selectInitialRootIOPS(state.game),
  );
  const currentRootIOPS = useAppSelector((state) =>
    selectRootBlockIOPS(state.game),
  );
  const trafficPattern = useAppSelector((state) =>
    selectTrafficPattern(state.game),
  );

  const iopsToDisplay =
    gameState.isStarted && currentRootIOPS ? currentRootIOPS : initialIOPS;

  const totalIOPS = iopsToDisplay?.total || 0;
  const readTotal =
    iopsToDisplay?.byOperationType[OperationTypes.READ]?.total || 0;
  const readPct = totalIOPS > 0 ? Math.round((readTotal / totalIOPS) * 100) : 0;
  const writePct = 100 - readPct;

  const handleStartPauseClick = useCallback(() => {
    if (!gameState.isStarted) {
      trackEvent(GA_EVENTS.GAME_STARTED);
      startGame();
    } else if (gameState.isRunning) {
      trackEvent(GA_EVENTS.GAME_PAUSED);
      pauseGame();
    } else {
      trackEvent(GA_EVENTS.GAME_RESUMED);
      resumeGame();
    }
  }, [gameState, pauseGame, startGame, resumeGame]);

  const handleSettingsClick = useCallback(() => {
    if (gameState.isRunning) {
      trackEvent(GA_EVENTS.GAME_PAUSED);
      pauseGame();
    }
    trackEvent(GA_EVENTS.IOPS_MODAL_OPENED);
  }, [gameState.isRunning, pauseGame]);

  const handleResetConfirm = useCallback(() => {
    trackEvent(GA_EVENTS.CANVAS_RESET);
    setCurrentDesignName(null);
    setDesignName(null);
    resetGame();
  }, [resetGame]);

  return (
    <Card
      style={{
        position: "absolute",
        top: isMobile ? '10px' : "10px",
        left: isMobile ? '10px' : "10px",
        right: isMobile ? '10px' : "10px",
        display: "flex",
        flexDirection: !isDesktop ? "column" : "row",
        justifyContent: "space-between",
        gap: isMobile ? "4px" : "10px",
        zIndex: 100,
        padding: isMobile ? "10px 15px" : "5px 16px",
        alignItems: !isDesktop ? "stretch" : "center",
      }}
      elevation={5}
    >
      {/* Row 1: Logo + Design name + IOPS display */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minWidth: 0,
          }}
        >
          <img
            src="/logo100.png"
            alt="System Design Playground Logo"
            style={{
              width: isMobile ? "24px" : "32px",
              height: isMobile ? "24px" : "32px",
              objectFit: "contain",
              filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
              flexShrink: 0,
            }}
          />
          {(isDesktop) && (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: COLOR_PRIMARY,
                letterSpacing: "-0.3px",
                whiteSpace: "nowrap",
              }}
            >
              System Design Playground
            </Typography>
          )}
          {/* Design Name Label */}
          <Chip
            label={designName || "Scratchpad"}
            size="small"
            variant={designName ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              color: designName ? "white" : COLOR_PRIMARY,
              backgroundColor: designName ? COLOR_PRIMARY : "transparent",
              borderColor: COLOR_PRIMARY,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        </Box>

        {(isMobile || isTablet) && (
          <IOPSDisplay
            totalIOPS={totalIOPS}
            readPct={readPct}
            writePct={writePct}
            trafficPattern={trafficPattern}
            isMobile={isMobile}
          />
        )}  
      </Box>

      {/* Row 2: Timer + Action buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: isMobile ? "4px" : "10px",
          flexShrink: 0,
          justifyContent: isMobile ? "space-between" : "flex-end",
        }}
      >
        <Typography
          variant="h2"
          style={{
            color: COLOR_PRIMARY,
            fontSize: isMobile ? "22px" : "32px",
            fontWeight: 800,
            display: "none", // Hidden for now
          }}
        >
          Lv.{gameState.level}
        </Typography>
        <Typography
          variant="h2"
          style={{
            color: "#077620",
            fontSize: isMobile ? "22px" : "32px",
            fontWeight: 800,
            display: "none", // Hidden for now
          }}
        >
          ${gameState.accountBalance.toFixed(2)}
        </Typography>
        {isDesktop && (
          <IOPSDisplay
            totalIOPS={totalIOPS}
            readPct={readPct}
            writePct={writePct}
            trafficPattern={trafficPattern}
            isMobile={isMobile}
          />
        )}
        <GameTimer
          elapsedTime={gameState.timeElapsedInSec}
          isRunning={gameState.isRunning}
          isStarted={gameState.isStarted}
        />

        <ScoreBoardActions
          isMobile={isMobile}
          isRunning={gameState.isRunning}
          onSettingsClick={handleSettingsClick}
          onPlayPauseClick={handleStartPauseClick}
          onResetConfirm={handleResetConfirm}
          onDesignSaved={(name) => setDesignName(name)}
          onDesignLoaded={(name) => setDesignName(name)}
        />
      </Box>
    </Card>
  );
};

export default ScoreBoard;
